import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "../../ui/dialog";
import styles from "./styles.module.css";

/**
 * OOR Report lifecycle popups (SCRUM-191 / SCRUM-192), built on the shared
 * Dialog primitive (overlay + Esc/click-outside close + close X) but with
 * fully custom inner markup so they match the Figma cards exactly:
 *   - Mark Submitted        (Figma 2286-15703)
 *   - post-share prompt      (Figma 1833-1127)  -> same component, different copy
 *   - post-download prompt   (Figma 2142-2099)  -> same component, different copy
 *   - Close Report           (Figma 1810-464 collapsed / 1805-418 expanded)
 *
 * The note captured here is frontend-only for now; persisting it lands with
 * the SCRUM-191/192 backend (Arooj). onConfirm receives it so the wiring is
 * ready.
 */

/**
 * Mark-Submitted-style confirm. Reused for the direct "Mark as Submitted"
 * action and for the post-share / post-download prompts (which only differ in
 * `message` and the secondary button `cancelLabel`).
 */
export const MarkSubmittedDialog = ({
  open,
  setOpen,
  message,
  cancelLabel = "Cancel",
  confirmLabel = "Mark as Submitted",
  onConfirm,
  onCancel,
}) => {
  const [note, setNote] = useState("");

  // Reset the note whenever the dialog (re)opens.
  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  const handleCancel = () => {
    setOpen(false);
    if (onCancel) onCancel();
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm(note);
  };

  return (
    <Dialog open={open} setOpen={setOpen}>
      <DialogContent className={styles.lifecyclePopup}>
        <div className={styles.popupTitle}>Mark Submitted</div>
        <div className={styles.popupDivider} />
        <div className={styles.popupBody}>
          <p className={styles.popupMessage}>{message}</p>
          <div className={styles.popupNoteLabel}>Add a note</div>
          <textarea
            className={styles.popupNote}
            placeholder="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className={styles.popupFooter}>
          <button
            type="button"
            className={`${styles.popupBtn} ${styles.popupBtnSecondary}`}
            onClick={handleCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`${styles.popupBtn} ${styles.popupBtnPrimary}`}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Close Report confirm. Starts collapsed with an "Add a note" link; clicking
 * it reveals the note box (Figma collapsed -> expanded variants). Two terminal
 * actions: close without submitting, or close and mark submitted.
 */
export const CloseReportDialog = ({
  open,
  setOpen,
  onCloseWithoutSubmitting,
  onCloseAndSubmit,
}) => {
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);

  useEffect(() => {
    if (open) {
      setNote("");
      setShowNote(false);
    }
  }, [open]);

  return (
    <Dialog open={open} setOpen={setOpen}>
      <DialogContent className={styles.lifecyclePopup}>
        <div className={styles.popupTitle}>Close Report</div>
        <div className={styles.popupDivider} />
        <div className={styles.popupBody}>
          <p className={styles.popupMessage}>
            Are you sure you want to close the report?
          </p>
          {showNote ? (
            <>
              <div className={styles.popupNoteLabel}>Add a note</div>
              <textarea
                className={styles.popupNote}
                placeholder="Note"
                autoFocus
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </>
          ) : (
            <button
              type="button"
              className={styles.popupLink}
              onClick={() => setShowNote(true)}
            >
              Add a note
            </button>
          )}
        </div>
        <div className={styles.popupFooter}>
          <button
            type="button"
            className={`${styles.popupBtn} ${styles.popupBtnDanger}`}
            onClick={() => onCloseWithoutSubmitting && onCloseWithoutSubmitting(note)}
          >
            Close Without Submitting
          </button>
          <button
            type="button"
            className={`${styles.popupBtn} ${styles.popupBtnPrimary}`}
            onClick={() => onCloseAndSubmit && onCloseAndSubmit(note)}
          >
            Close &amp; Mark as Submitted
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
